import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalContent,
  Spinner,
  Select,
  SelectItem,
  Tooltip,
  Accordion,
  AccordionItem,
} from "@heroui/react";
import { ResourceType } from "shared/types/resource";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import SearchComponent from "shared/components/searchBar/components/searchComponent";
import WhatsAppTemplateSelector from "shared/components/whatsapp/templateSelector";
import {
  Phone,
  Building,
  PaperPlaneTilt,
  XCircle,
  GlobeSimple,
  Eye,
  UsersThree,
  WhatsappLogo,
  FileTxt,
} from "@phosphor-icons/react";
import { replaceTemplateParams, getTemplateBody } from "../util/template-util";
import ParameterSection from "./parameterSection";
import ButtonNew from "components/buttonNew/buttonNew";
import { toast } from "react-toastify";

interface Contact {
  id: string;
  name: string;
  resourceId: string;
  language?: string;
  firstName: string;
  phoneMobile?: string;
}

export interface TemplateParam {
  key: string;
  isGlobal: boolean;
  globalValue: string;
  personalValues: Record<string, string>;
}

const SUPPORTED_LANGUAGES = ["en", "nl", "fr"];

interface InitialParticipant {
  phone: string;
  contactId?: string;
  companyId?: string;
  name?: string;
}

interface AddConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: any;
  onAdd: (
    participants: Contact[],
    metadata: any,
    templates: Record<string, any>,
    templateParams: TemplateParam[],
    conversationType: string,
  ) => void;
  initialParticipant?: InitialParticipant | null;
}

const AddConversationModal: React.FC<AddConversationModalProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  onAdd,
  initialParticipant,
}) => {
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const { session } = useSession();
  const [metadata, setMetadata] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [participants, setParticipants] = useState<Contact[]>([]);
  const [conversationType] = useState<string>("individual");
  const [selectedTemplates, setSelectedTemplates] = useState<
    Record<string, any>
  >({});
  const [templateParams, setTemplateParams] = useState<TemplateParam[]>([]);
  const [initialParticipantProcessed, setInitialParticipantProcessed] = useState(false);

  // Handle initial participant from URL params (deep linking)
  useEffect(() => {
    if (isOpen && initialParticipant && !initialParticipantProcessed) {
      setInitialParticipantProcessed(true);

      // If contactId is provided, fetch the contact details
      if (initialParticipant.contactId) {
        fetchContactById(initialParticipant.contactId);
      } else if (initialParticipant.phone) {
        // Create a temporary participant with just the phone number
        const tempParticipant: Contact = {
          id: `temp-${Date.now()}`,
          name: initialParticipant.name || initialParticipant.phone,
          resourceId: "",
          firstName: initialParticipant.name?.split(" ")[0] || "",
          phoneMobile: initialParticipant.phone,
        };
        setParticipants([tempParticipant]);
      }
    }

    // Reset when modal closes
    if (!isOpen) {
      setInitialParticipantProcessed(false);
    }
  }, [isOpen, initialParticipant, initialParticipantProcessed]);

  const fetchContactById = async (contactId: string) => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/contact/${contactId}`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const contact = await response.json();
        const participant: Contact = {
          id: contact.id?.toString() || contactId,
          name: `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
          resourceId: contact.id?.toString() || contactId,
          firstName: contact.firstName || "",
          phoneMobile: contact.phoneMobile || initialParticipant?.phone,
          language: contact.language,
        };
        setParticipants([participant]);
      }
    } catch (error) {
      console.error("Error fetching contact:", error);
      toast.error("Kon contact niet ophalen");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBuilding) {
      fetchBuildingContacts(selectedBuilding.resourceId);
    }
  }, [selectedBuilding]);

  useEffect(() => {
    if (participants.length > 0) {
      setTemplateParams((prevParams) =>
        prevParams.map((param) => ({
          ...param,
          personalValues: participants.reduce(
            (acc, participant) => ({
              ...acc,
              [participant.id]:
                param.key === "1"
                  ? participant.firstName ||
                    param.personalValues[participant.id] ||
                    ""
                  : param.personalValues[participant.id] || "",
            }),
            {},
          ),
        })),
      );
    }
  }, [participants]);

  const fetchBuildingContacts = async (buildingId: string) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/building/${buildingId}`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const contracts = await response.json();
        const contactsSet = new Set<Contact>();
        contracts.forEach((contract: any) => {
          contract.contractContacts.forEach((contact: any) => {
            if (contact && contact.resource) {
              contactsSet.add({
                id: contact.resource.id,
                firstName: contact.firstName,
                name: contact.resource.name,
                resourceId: contact.resourceId,
                phoneMobile: contact.resource.metadata1,
                language:
                  contact && contact.language && contact.language == "nl_BE"
                    ? "nl"
                    : contact?.language == "en_US"
                      ? "en"
                      : contact?.language == "fr_FR"
                        ? "fr"
                        : "nl",
              });
            }
          });
        });
        setParticipants(Array.from(contactsSet));
        updateMetadata(buildingId, contracts);
      }
    } catch (error) {
      console.error("Error fetching building contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTemplateParamsForNewParticipant = (newParticipant: Contact) => {
    setTemplateParams((prevParams) =>
      prevParams.map((param) => ({
        ...param,
        personalValues: {
          ...param.personalValues,
          [newParticipant.id]:
            param.key === "1" ? newParticipant.firstName || "" : "",
        },
      })),
    );
  };

  const updateMetadata = (buildingId: string, contracts: any[]) => {
    const unitIds = new Set<string>();
    contracts.forEach((contract) => {
      if (contract.unit) {
        unitIds.add(contract.unit.id);
      }
    });
    setMetadata((prevMetadata: any) => ({
      ...prevMetadata,
      buildingId,
      unitIds: Array.from(unitIds),
    }));
  };

  const handleAddParticipant = (resource: any) => {
    let contactDetails: Contact | null = null;
    if (resource && resource.contactDetails) {
      contactDetails = resource.contactDetails;
    }

    if (resource.type === ResourceType.BUILDING) {
      setSelectedBuilding(resource);
    } else if (resource.type === ResourceType.CONTACT) {
      const language =
        contactDetails?.language === "nl_BE"
          ? "nl"
          : contactDetails?.language === "en_US"
            ? "en"
            : contactDetails?.language === "fr_FR"
              ? "fr"
              : "nl";

      const newParticipant = {
        ...contactDetails,
        id: resource.id,
        name: resource.name,
        resourceId: resource.resourceId,
        phoneMobile: contactDetails
          ? contactDetails.phoneMobile ||
            (contactDetails as any).phone_mobile ||
            "GSM-nummer onbekend"
          : resource.metadata1 || "GSM nummer onbekend",
        language: language,
        firstName: contactDetails?.firstName || "",
      };

      setParticipants((prev) => [...prev, newParticipant]);
      updateTemplateParamsForNewParticipant(newParticipant);
      fetchContactRelations(resource.resourceId);
    }
  };

  const fetchContactRelations = async (resourceId: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/contact/${resourceId}`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const relations = await response.json();
        if (relations.unit) {
          setMetadata((prevMetadata: any) => ({
            ...prevMetadata,
            unitIds: [...(prevMetadata.unitIds || []), relations.unit.id],
            buildingId: relations.unit.building.id,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching contact relations:", error);
    }
  };

  const handleRemoveParticipant = (id: string) => {
    setParticipants((prev) => prev.filter((p) => p.id !== id));
  };

  const handleLanguageChange = (participantId: string, language: string) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === participantId ? { ...p, language } : p)),
    );
  };

  const handleSelectTemplate = (
    language: string,
    template: any,
    params: Record<string, string>,
  ) => {
    if (uniqueLanguages.includes(language)) {
      setSelectedTemplates((prev) => ({ ...prev, [language]: template }));

      const newTemplateParams = Object.entries(params).map(([key, value]) => ({
        key,
        isGlobal: false,
        globalValue: "",
        personalValues: participants.reduce(
          (acc, p) => ({
            ...acc,
            [p.id]: key === "1" ? p.firstName || value : value,
          }),
          {},
        ),
      }));

      setTemplateParams((prevParams) => {
        const mergedParams = [...prevParams];
        newTemplateParams.forEach((newParam) => {
          const existingParamIndex = mergedParams.findIndex(
            (p) => p.key === newParam.key,
          );
          if (existingParamIndex === -1) {
            mergedParams.push(newParam);
          } else {
            mergedParams[existingParamIndex] = {
              ...mergedParams[existingParamIndex],
              personalValues: {
                ...mergedParams[existingParamIndex].personalValues,
                ...newParam.personalValues,
              },
            };
          }
        });
        return mergedParams;
      });
    }
  };

  const toggleParamGlobal = (paramKey: string) => {
    setTemplateParams((prev) =>
      prev.map((param) =>
        param.key === paramKey
          ? { ...param, isGlobal: !param.isGlobal }
          : param,
      ),
    );
  };

  const handleGlobalParamChange = (paramKey: string, value: string) => {
    setTemplateParams((prev) =>
      prev.map((param) =>
        param.key === paramKey ? { ...param, globalValue: value } : param,
      ),
    );
  };

  const getUniqueLanguages = () => {
    return Array.from(new Set(participants.map((p) => p.language)));
  };

  const uniqueLanguages = getUniqueLanguages();

  const handlePersonalParamChange = (
    paramKey: string,
    participantId: string,
    value: string,
  ) => {
    setTemplateParams((prev) =>
      prev.map((param) =>
        param.key === paramKey
          ? {
              ...param,
              personalValues: {
                ...param.personalValues,
                [participantId]: value,
              },
            }
          : param,
      ),
    );
  };

  const getTemplatePreview = (language: string) => {
    const template = selectedTemplates[language];
    console.log("Getting template preview for language", language);
    if (!template) return "";
    console.log("Template found", template);

    const templateBody = getTemplateBody(template);
    if (!templateBody) return "";

    const previewParams = templateParams.reduce(
      (acc, param) => {
        acc[param.key] = param.isGlobal ? param.globalValue : `[${param.key}]`;
        return acc;
      },
      {} as Record<string, string>,
    );

    return replaceTemplateParams(templateBody, previewParams);
  };

  const handleSubmit = () => {
    // Validate that all template parameters are filled in
    const hasEmptyParams = templateParams.some((param) => {
      if (param.isGlobal) {
        // Check if global value is empty
        return !param.globalValue || param.globalValue.trim() === '';
      } else {
        // Check if any personal value is empty
        return participants.some(
          (p) => !param.personalValues[p.id] || param.personalValues[p.id].trim() === ''
        );
      }
    });

    if (hasEmptyParams) {
      toast.error('Vul alle template parameters in voordat je het bericht verstuurt.');
      return;
    }

    const usedTemplates = Object.fromEntries(
      Object.entries(selectedTemplates).filter(([lang]) =>
        uniqueLanguages.includes(lang),
      ),
    );
    onAdd(
      participants,
      metadata,
      usedTemplates,
      templateParams,
      conversationType,
    );
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      className="h-[90vh] max-h-[90vh] p-4"
    >
      <ModalContent>
        <ModalHeader>
          <h3 className="font-semibold text-lg">
            Nieuwe Whatsapp conversatie aanmaken
          </h3>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <Accordion defaultExpandedKeys={["1"]}>
              <AccordionItem
                key="1"
                aria-label="Contactpersonen"
                startContent={
                  <div className="rounded-full bg-neutral-primary/10 p-2 text-neutral-primary">
                    <UsersThree size={24} weight="duotone" />
                  </div>
                }
                subtitle="Zoek en bekijk geselecteerde contactpersonen"
                title="Contactpersonen"
              >
                <div className="flex min-h-[300px] flex-col gap-4">
                  <header>
                    <SearchComponent
                      resourceFilter={[
                        ResourceType.CONTACT,
                        ResourceType.BUILDING,
                      ]}
                      onSelect={handleAddParticipant}
                      label="Zoek op naam of gebouwcode"
                    />
                  </header>
                  {selectedBuilding && (
                    <div className="flex items-center justify-between">
                      <div className="flex w-fit items-center gap-2 rounded-full border border-neutral-primary/20 p-2 text-sm text-neutral-primary">
                        <div className="flex items-center gap-1">
                          <Building size={14} />
                          <span>{selectedBuilding.name}</span>
                        </div>
                        <Tooltip content="Verwijder uit conversatie">
                          <a
                            onClick={() => setSelectedBuilding(null)}
                            className="cursor-pointer text-status-bad"
                          >
                            <XCircle size={20} weight="duotone" />
                          </a>
                        </Tooltip>
                      </div>
                      {isLoading && (
                        <div className="flex items-center justify-center gap-2 text-sm font-normal">
                          <Spinner size="sm" color="success" />
                          Contactpersonen ophalen...
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid gap-2 sm:grid-cols-3">
                    {participants.map((participant) => (
                      <div
                        key={participant.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-neutral-primary/10 p-2 text-sm text-neutral-primary"
                      >
                        <div>
                          <span>{participant.name}</span>
                          {participant.phoneMobile && (
                            <div className="flex items-center gap-1 text-sm text-neutral-secondary-mid">
                              <Phone size={16} />
                              {participant.phoneMobile}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={
                              participant.language &&
                              participant.language == "nl_BE"
                                ? "nl"
                                : participant.language == "en_US"
                                  ? "en"
                                  : participant.language == "fr_FR"
                                    ? "fr"
                                    : "nl"
                            }
                            defaultSelectedKeys={[
                              participant.language &&
                              participant.language == "nl_BE"
                                ? "nl"
                                : participant.language == "en_US"
                                  ? "en"
                                  : participant.language == "fr_FR"
                                    ? "fr"
                                    : "nl",
                            ]}
                            onChange={(e) =>
                              handleLanguageChange(
                                participant.id,
                                e.target.value,
                              )
                            }
                            classNames={{
                              base: "w-20",
                              label: "text-neutral-primary",
                              value: "text-neutral-primary",
                              trigger:
                                "rounded-sm bg-white shadow-form-element! data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv! group-data-[focus=true]:shadow-form-element-fc!",
                            }}
                          >
                            {SUPPORTED_LANGUAGES.map((lang: any) => (
                              <SelectItem key={lang}>
                                {lang.toUpperCase()}
                              </SelectItem>
                            ))}
                          </Select>
                          <Tooltip content="Verwijder uit conversatie">
                            <a
                              onClick={() =>
                                handleRemoveParticipant(participant.id)
                              }
                              className="cursor-pointer text-status-bad"
                            >
                              <XCircle size={20} weight="duotone" />
                            </a>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AccordionItem>
              <AccordionItem
                key="2"
                aria-label="whatsappTemplate"
                startContent={
                  <div className="rounded-full bg-neutral-primary/10 p-2 text-neutral-primary">
                    <WhatsappLogo size={24} weight="duotone" />
                  </div>
                }
                subtitle="Selecteer een template en wijzig het bericht"
                title="Bericht"
              >
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-1 font-medium">
                      <FileTxt size={18} weight="duotone" />
                      <p>Template</p>
                    </div>
                    {uniqueLanguages.map((lang: any) => (
                      <div className="rounded-lg border border-neutral-primary/20 p-3">
                        <div
                          key={lang}
                          className="flex flex-col gap-4 divide-y divide-neutral-primary/20"
                        >
                          <header className="flex flex-col gap-1">
                            <p className="flex items-center gap-1 font-medium">
                              <GlobeSimple size={18} weight="duotone" />
                              {lang.toUpperCase()}
                            </p>
                            <WhatsAppTemplateSelector
                              onSelectTemplate={(template, params) =>
                                handleSelectTemplate(lang, template, params)
                              }
                              incomingSelectedTemplate={
                                selectedTemplates[lang]?.sid
                              }
                            />
                          </header>
                          {selectedTemplates[lang] && (
                            <div className="flex flex-col gap-2 pt-3">
                              <p className="flex items-center gap-1 text-sm font-medium uppercase text-neutral-primary/60">
                                <Eye size={18} weight="duotone" />
                                Template Preview
                              </p>
                              <p className="font-normal">
                                {getTemplatePreview(lang)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <ParameterSection
                    templateParams={templateParams}
                    participants={participants}
                    toggleParamGlobal={toggleParamGlobal}
                    handleGlobalParamChange={handleGlobalParamChange}
                    handlePersonalParamChange={handlePersonalParamChange}
                  />
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonNew
            variant="secondaryNeutral"
            onClick={onClose}
            // icon={<Archive weight="duotone" />}
            label="Sluiten"
          />
          <ButtonNew
            variant="primaryMoss"
            onClick={handleSubmit}
            icon={<PaperPlaneTilt weight="duotone" />}
            label="Bericht(en) versturen"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddConversationModal;
